import React from 'react';
import {
  Activity, Users, MessageSquare, Mic, Camera, Sun,
  QrCode, Sparkles, BookOpen, Mail
} from 'lucide-react';

const formatDateTime = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const MetricCard = ({ icon: Icon, label, value, sub, testid }) => (
  <div
    data-testid={testid}
    className="rounded-xl border border-slate-800/40 bg-slate-900/30 p-4 flex flex-col gap-2"
  >
    <div className="flex items-center gap-2 text-slate-400 text-xs uppercase tracking-wider">
      <Icon className="h-3.5 w-3.5" />
      <span>{label}</span>
    </div>
    <div className="text-2xl font-light text-slate-100">{value}</div>
    {sub && <div className="text-xs text-slate-500">{sub}</div>}
  </div>
);

/**
 * MetricsDetailBody renders a single event's engagement metrics.
 * Expects `metrics` to already be loaded by the parent (no fetching here).
 */
const MetricsDetailBody = ({ metrics }) => {
  if (!metrics) return null;
  const { portal, personal_notes, wishes, voices, photos, reveal, event } = metrics;
  const portalRate = portal.total_guests_on_list > 0
    ? Math.round((portal.unique_guests_seen / portal.total_guests_on_list) * 100)
    : 0;

  return (
    <div className="space-y-6" data-testid="metrics-detail-body">
      {/* Section: Portal Visits */}
      <section>
        <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-2">
          <QrCode className="h-3.5 w-3.5" /> Portal visits (QR scans + link opens)
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricCard
            icon={Activity}
            label="Total hits"
            value={portal.total_hits}
            sub="Every portal load"
            testid="metric-portal-hits"
          />
          <MetricCard
            icon={Users}
            label="Unique guests seen"
            value={portal.unique_guests_seen}
            sub={`${portalRate}% of ${portal.total_guests_on_list} on list`}
            testid="metric-unique-guests"
          />
          <MetricCard
            icon={Sparkles}
            label="First visit"
            value={portal.first_visited_at ? formatDateTime(portal.first_visited_at).split(',')[0] : '—'}
            sub={portal.first_visited_at ? formatDateTime(portal.first_visited_at).split(',').slice(1).join(',').trim() : 'Not yet visited'}
            testid="metric-first-visit"
          />
          <MetricCard
            icon={Sparkles}
            label="Last visit"
            value={portal.last_visited_at ? formatDateTime(portal.last_visited_at).split(',')[0] : '—'}
            sub={portal.last_visited_at ? formatDateTime(portal.last_visited_at).split(',').slice(1).join(',').trim() : '—'}
            testid="metric-last-visit"
          />
        </div>
      </section>

      {/* Section: Personal touches from the couple */}
      <section>
        <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-2">
          <Sparkles className="h-3.5 w-3.5" /> Personal touches from the couple
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricCard
            icon={MessageSquare}
            label="Notes per guest"
            value={personal_notes.text_per_guest ?? personal_notes.count ?? 0}
            sub="Text notes set in Studio"
            testid="metric-personal-notes-text"
          />
          <MetricCard
            icon={Mic}
            label="Voices per guest"
            value={personal_notes.voice_per_guest ?? 0}
            sub='"In their voice" recordings'
            testid="metric-personal-notes-voice"
          />
          <MetricCard
            icon={MessageSquare}
            label="Table messages"
            value={personal_notes.text_per_table ?? 0}
            sub="Per-table text notes"
            testid="metric-personal-notes-table-text"
          />
          <MetricCard
            icon={Mic}
            label="Table voices"
            value={personal_notes.voice_per_table ?? 0}
            sub="Per-table voice notes"
            testid="metric-personal-notes-table-voice"
          />
        </div>
      </section>

      {/* Section: Guest contributions */}
      <section>
        <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-2">
          <MessageSquare className="h-3.5 w-3.5" /> Guest contributions
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricCard
            icon={MessageSquare}
            label="Wishes (text)"
            value={wishes.total}
            sub={wishes.by_organizer_proxy > 0
              ? `${wishes.by_guests} by guests · ${wishes.by_organizer_proxy} added by you`
              : `${wishes.by_guests} by guests`}
            testid="metric-wishes"
          />
          <MetricCard
            icon={Mic}
            label="Voice notes"
            value={voices.total}
            sub={voices.by_organizer_proxy > 0
              ? `${voices.by_guests} by guests · ${voices.by_organizer_proxy} added by you`
              : `${voices.by_guests} by guests`}
            testid="metric-voices"
          />
          <MetricCard
            icon={Camera}
            label="Photos shared"
            value={photos.total}
            sub={photos.by_organizer_proxy > 0
              ? `${photos.by_guests} by guests · ${photos.by_organizer_proxy} added by you`
              : `${photos.by_guests} by guests`}
            testid="metric-photos"
          />
          <MetricCard
            icon={Sparkles}
            label="Total contributions"
            value={(wishes.total || 0) + (voices.total || 0) + (photos.total || 0)}
            sub="Wishes + voices + photos"
            testid="metric-contributions-total"
          />
        </div>
      </section>

      {/* Section: Morning After Reveal */}
      <section>
        <h3 className="text-xs uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-2">
          <Sun className="h-3.5 w-3.5" /> Morning After Reveal
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MetricCard
            icon={Sun}
            label="Status"
            value={reveal.has_been_accessed ? 'Opened' : 'Not opened'}
            sub={reveal.has_been_accessed
              ? `${reveal.open_count} total ${reveal.open_count === 1 ? 'open' : 'opens'}`
              : 'Couple has not yet visited'}
            testid="metric-reveal-status"
          />
          <MetricCard
            icon={Activity}
            label="First accessed"
            value={reveal.first_accessed_at
              ? formatDateTime(reveal.first_accessed_at).split(',')[0]
              : '—'}
            sub={reveal.first_accessed_at
              ? formatDateTime(reveal.first_accessed_at).split(',').slice(1).join(',').trim()
              : '—'}
            testid="metric-reveal-first"
          />
          <MetricCard
            icon={Sparkles}
            label="Kept items"
            value={reveal.kept_count}
            sub="Moments held onto"
            testid="metric-reveal-kept"
          />
          <MetricCard
            icon={BookOpen}
            label="Books sent"
            value={reveal.books_sent}
            sub={reveal.book_last_sent_at
              ? `Last: ${formatDateTime(reveal.book_last_sent_at)}`
              : 'Never'}
            testid="metric-books-sent"
          />
        </div>

        {/* Recipients list */}
        {reveal.recipients && reveal.recipients.length > 0 && (
          <div className="mt-4 rounded-xl border border-slate-800/40 bg-slate-900/30 p-4">
            <div className="flex items-center gap-2 text-slate-400 text-xs uppercase tracking-wider mb-2">
              <Mail className="h-3.5 w-3.5" />
              <span>Book recipients</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {reveal.recipients.map((email, i) => (
                <span
                  key={i}
                  className="text-xs px-2 py-1 rounded-md bg-slate-800/60 text-slate-300"
                >
                  {email}
                </span>
              ))}
            </div>
          </div>
        )}
      </section>

      {/* Event meta footer */}
      <div className="pt-4 border-t border-slate-800/40 text-xs text-slate-500 flex flex-wrap items-center justify-between gap-2">
        <span>
          {event.location && <span>{event.location} · </span>}
          {event.slug && <span>/{event.slug}</span>}
        </span>
        {event.owner && (
          <span>
            Owner: <span className="text-slate-300">{event.owner.name}</span> · {event.owner.email}
          </span>
        )}
      </div>
    </div>
  );
};

export { MetricsDetailBody };
export default MetricsDetailBody;
